import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import { Select, Tag } from "antd";
import SelectItem from "./SelectItem";
import DatePickerItem from "./DatePickerItem";

const { Option } = Select;

//Convierte lo que el usuario capturó en lo que espera el backend. Se manda "YYYY-MM-DD" y no el
//moment tal cual: el DatePicker le pega la hora actual a la fecha elegida, y una fecha con hora
//viajando a un servidor en UTC es exactamente el bug de "se me corrió un día" que este formato evita
//(el backend la ancla con parsearFechaDia, ver Utils/fechaDia.utils.js).
export const hospedajeParaPayload = (hospedaje) => {
  if (!hospedaje || !hospedaje.habitacionId || !hospedaje.fechaLlegada || !hospedaje.fechaSalida) {
    return null;
  }
  return {
    habitacionId: hospedaje.habitacionId,
    fechaLlegada: moment(hospedaje.fechaLlegada).format("YYYY-MM-DD"),
    fechaSalida: moment(hospedaje.fechaSalida).format("YYYY-MM-DD"),
  };
};

export const calcularNoches = (fechaLlegada, fechaSalida) => {
  if (!fechaLlegada || !fechaSalida) return 0;
  const noches = moment(fechaSalida).startOf("day").diff(moment(fechaLlegada).startOf("day"), "days");
  return noches > 0 ? noches : 0;
};

//Captura de la estancia de un renglón de venta de hospedaje: qué habitación y de cuándo a cuándo.
//Es un componente CONTROLADO (`value` / `onChange` con el objeto hospedaje completo) para poder
//montarse igual en Agregar Venta, que guarda todo en el Form de antd, y en Editar Remisión, que lo
//guarda en `state` — cada pantalla decide dónde vive el dato.
//
//Es lo único que alimenta el mapa de hotel: la ocupación no se guarda en ningún lado, se deriva de
//estas dos fechas (ver Controllers/Hoteleria/hoteleria.controllers.js/selectMapaHotel).
const FilaHospedajeVenta = ({
  value,
  onChange,
  habitaciones = [],
  insumoId,
  empresaId,
  headersToken,
  proyectoId,
  ventaId,
  // Al editar una venta programada: ni ella misma ni las ventas que ya generó cuentan como
  // conflicto consigo misma. Sin esto la habitación que la programada ya tiene asignada saldría
  // deshabilitada en cuanto se generara la primera ocurrencia.
  ventaProgramadaId,
  onNochesChange,
  disabled,
}) => {
  const [ocupadas, setOcupadas] = useState([]);
  // Lo que compromete una venta programada. Va aparte de `ocupadas` porque NO bloquea: una programada
  // todavía no es una venta, y quien está en el mostrador tiene que poder vender el cuarto de todos
  // modos. Solo se avisa.
  const [comprometidas, setComprometidas] = useState([]);

  const habitacionId = value?.habitacionId ? String(value.habitacionId) : undefined;
  const fechaLlegada = value?.fechaLlegada ? moment(value.fechaLlegada) : null;
  const fechaSalida = value?.fechaSalida ? moment(value.fechaSalida) : null;
  const noches = calcularNoches(fechaLlegada, fechaSalida);

  // Solo las habitaciones que se cobran con el insumo de este renglón: si el renglón vende "Sencilla",
  // ofrecer una suite haría que el mapa mostrara ocupada una habitación a la tarifa equivocada.
  const habitacionesDelInsumo = habitaciones.filter(
    (valor) => String(valor.insumoId) === String(insumoId)
  );

  const textoLlegada = fechaLlegada ? fechaLlegada.format("YYYY-MM-DD") : undefined;
  const textoSalida = fechaSalida ? fechaSalida.format("YYYY-MM-DD") : undefined;

  // Qué habitaciones ya están comprometidas en el rango capturado. Se consulta al backend en vez de
  // deducirlo del mapa porque el traslape hay que evaluarlo contra TODAS las ventas vivas, no contra
  // las de un solo día.
  const consultarOcupadas = useCallback(async () => {
    if (!textoLlegada || !textoSalida || noches <= 0) {
      setOcupadas([]);
      setComprometidas([]);
      return;
    }
    try {
      const res = await axios.post(
        "hoteleria/selectHabitacionesParaVenta",
        {
          empresaId,
          proyectoId,
          ventaId,
          ventaProgramadaId,
          fechaLlegada: textoLlegada,
          fechaSalida: textoSalida,
        },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        setOcupadas(res.data.ocupadas || []);
        setComprometidas(res.data.comprometidas || []);
      }
    } catch (error) {
      console.error("Error al consultar las habitaciones ocupadas:", error);
    }
  }, [
    empresaId,
    headersToken,
    proyectoId,
    ventaId,
    ventaProgramadaId,
    textoLlegada,
    textoSalida,
    noches,
  ]);

  useEffect(() => {
    consultarOcupadas();
  }, [consultarOcupadas]);

  const ocupacionDe = (id) => ocupadas.find((valor) => String(valor.habitacionId) === String(id));
  const compromisoDe = (id) =>
    comprometidas.find((valor) => String(valor.habitacionId) === String(id));

  const emitir = (cambios) => {
    const nuevoValor = {
      habitacionId,
      fechaLlegada: textoLlegada,
      fechaSalida: textoSalida,
      ...cambios,
    };

    // Si al mover la llegada la salida quedó antes o el mismo día, se limpia: vale más obligar a
    // recapturarla que dejar un rango inválido que el backend descartaría sin avisar.
    if (
      nuevoValor.fechaLlegada &&
      nuevoValor.fechaSalida &&
      moment(nuevoValor.fechaSalida).startOf("day") <= moment(nuevoValor.fechaLlegada).startOf("day")
    ) {
      nuevoValor.fechaSalida = undefined;
    }

    onChange && onChange(nuevoValor);

    const nochesNuevas = calcularNoches(nuevoValor.fechaLlegada, nuevoValor.fechaSalida);
    if (onNochesChange && nochesNuevas > 0 && nochesNuevas !== noches) onNochesChange(nochesNuevas);
  };

  const conflicto = habitacionId ? ocupacionDe(habitacionId) : null;
  const compromiso = habitacionId && !conflicto ? compromisoDe(habitacionId) : null;

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <span style={{ fontWeight: 700, color: "#005661" }}>Hospedaje:</span>

      <div>
        <span style={{ fontSize: 11, color: "#6b7280", marginRight: 4 }}>Habitación</span>
        <SelectItem
          width={190}
          size="small"
          placeholder="Habitación"
          value={habitacionId}
          disabled={disabled}
          allowClear
          onChange={(valor) => emitir({ habitacionId: valor })}
        >
          {habitacionesDelInsumo.map((valorHabitacion) => {
            const ocupada = ocupacionDe(valorHabitacion._id);
            const comprometida = !ocupada && compromisoDe(valorHabitacion._id);
            // La etiqueta se arma como UNA sola cadena, no como varios hijos: el filterOption de
            // SelectItem hace `option.children.toLowerCase()`, que truena si children es un arreglo.
            const etiqueta =
              `${valorHabitacion.numero}` +
              (valorHabitacion.nombre ? ` - ${valorHabitacion.nombre}` : "") +
              (valorHabitacion.nivelNombre ? ` (${valorHabitacion.nivelNombre})` : "") +
              (ocupada ? " — ocupada" : "") +
              (comprometida ? " — programada" : "");
            return (
              <Option
                key={valorHabitacion._id}
                value={String(valorHabitacion._id)}
                // Solo la venta real bloquea. La programada se puede sobreescribir a sabiendas.
                disabled={!!ocupada}
              >
                {etiqueta}
              </Option>
            );
          })}
        </SelectItem>
      </div>

      <div>
        <span style={{ fontSize: 11, color: "#6b7280", marginRight: 4 }}>Llegada</span>
        <DatePickerItem
          width={130}
          size="small"
          value={fechaLlegada}
          disabled={disabled}
          onChange={(valor) =>
            emitir({ fechaLlegada: valor ? valor.format("YYYY-MM-DD") : undefined })
          }
        />
      </div>

      <div>
        <span style={{ fontSize: 11, color: "#6b7280", marginRight: 4 }}>Salida</span>
        <DatePickerItem
          width={130}
          size="small"
          value={fechaSalida}
          disabled={disabled}
          // La salida tiene que ser al menos el día siguiente: una estancia de cero noches no ocuparía
          // ninguna fecha en el mapa y el backend la descartaría en silencio.
          disabledDate={(fecha) =>
            fechaLlegada ? fecha && fecha <= moment(fechaLlegada).startOf("day") : false
          }
          onChange={(valor) =>
            emitir({ fechaSalida: valor ? valor.format("YYYY-MM-DD") : undefined })
          }
        />
      </div>

      <Tag color={noches > 0 ? "cyan" : "default"}>
        {noches > 0 ? `${noches} noche(s)` : "Sin noches"}
      </Tag>

      {habitacionesDelInsumo.length === 0 && (
        <span style={{ fontSize: 11, color: "#b45309" }}>
          Ninguna habitación está configurada con este insumo. Asígnalo en la configuración del mapa de
          hotel.
        </span>
      )}

      {conflicto && (
        <span style={{ fontSize: 11, color: "#b91c1c", fontWeight: 700 }}>
          Esa habitación ya está ocupada en estas fechas
          {conflicto.folios?.length ? ` (${conflicto.folios.join(", ")})` : ""}
        </span>
      )}

      {/* Ámbar y no rojo: es un aviso, no un error. La venta programada todavía no existe como venta,
          así que vender el cuarto puede ser lo correcto —pero hay que saberlo antes. */}
      {compromiso && (
        <span style={{ fontSize: 11, color: "#b45309", fontWeight: 700 }}>
          Esa habitación tiene una venta programada en estas fechas
          {compromiso.folios?.length ? ` (${compromiso.folios.join(", ")})` : ""}
        </span>
      )}
    </div>
  );
};

export default FilaHospedajeVenta;
